import { useConversation, ConversationControls } from "./ConversationControls";
import { MAX_ATTACHMENT_BYTES } from '../../../../shared/uploadLimits';
import FeishuIcon from './FeishuIcon';
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { PaperPlaneRight, Stop, Robot, Sparkle, Microphone, MicrophoneSlash, Paperclip, ChatCircle, X, FileText, FolderSimple, SpinnerGap, CloudArrowDown, CaretDown, CaretRight, PencilSimple } from '@phosphor-icons/react';
import {
  parseInternalBlocks,
  hasInternalBlock,
  hasUnclosedInternal,
} from '../utils/internalBlocks';
import ChatInternalBlock from './ChatInternalBlock';

/** Confluence brand mark SVG */
const ConfluenceIcon = ({ size = 16, color }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 256 246" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="cfi-a" x1="99%" y1="63%" x2="35%" y2="94%">
        <stop offset="18%" stopColor={color || 'var(--ds-accent)'} />
        <stop offset="100%" stopColor={color || 'var(--ds-accent)'} />
      </linearGradient>
      <linearGradient id="cfi-b" x1="1%" y1="37%" x2="65%" y2="6%">
        <stop offset="18%" stopColor={color || 'var(--ds-accent)'} />
        <stop offset="100%" stopColor={color || 'var(--ds-accent)'} />
      </linearGradient>
    </defs>
    <path d="M9.26 187.77c-3.68 6.1-7.81 13.11-10.63 18.16a7.26 7.26 0 0 0 2.88 9.86l68.25 40.61a7.28 7.28 0 0 0 9.94-2.53c2.35-3.93 5.42-9.23 8.83-15.18 24.26-42.36 48.67-37.31 93.05-16.64l67.48 31.27a7.27 7.27 0 0 0 9.68-3.72l31.56-71.45a7.27 7.27 0 0 0-3.67-9.52c-17.64-8.18-52.86-24.51-74.2-34.39C130.37 97.6 58.34 104.87 9.26 187.77Z" fill="url(#cfi-a)" />
    <path d="M246.11 58.24c3.68-6.1 7.81-13.11 10.63-18.17a7.26 7.26 0 0 0-2.88-9.85L185.61-10.4a7.28 7.28 0 0 0-9.94 2.54c-2.35 3.92-5.42 9.23-8.83 15.17-24.26 42.37-48.67 37.32-93.05 16.65L6.31-7.32A7.27 7.27 0 0 0-3.38-3.6l-31.55 71.45a7.27 7.27 0 0 0 3.67 9.52c17.63 8.18 52.85 24.51 74.2 34.39 82.04 36.64 154.08 29.37 203.17-53.52Z" fill="url(#cfi-b)" />
  </svg>
);
import type { Profile } from '../types';
import { marked } from 'marked';
import { apiFetch } from '../api/auth-fetch';
import {
  uploadChatAttachment,
  chatAttachmentDownloadUrl,
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_PER_TURN,
  type ChatAttachment as ChatAttachmentRef,
} from '../api/client';

export type { ChatAttachmentRef };

export interface ChatMessage {
  server_message_id?:string;
  turn_id?:string;
  author_user_id?: string;
  author_name?: string;
  client_request_id?: string;
  intent?: string;
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  displayContent?: string; // shown in UI instead of content (e.g. page title vs agent instruction)
  messageType?: 'file' | 'folder'; // renders as file/folder card instead of text bubble
  /**
   * Content-addressed artifact reference for `messageType: 'file'`
   * cards that came in as a `Block::File` from the agent's IM SDK.
   * When set, the file card is clickable: it opens
   * `/api/decks/:deckId/artifacts/:fileHash` (with `?download=1` for
   * non-renderable mimes). Mutually exclusive with `fileKey` — render
   * code prefers fileHash when both happen to be set.
   */
  fileHash?: string;
  /**
   * Path-addressed (context key) reference for `messageType: 'file'`
   * cards expanded from `[REF:file] <key>` text markers — used when
   * the message didn't arrive as a typed file block (e.g. agent-
   * emitted [REF:file] artifacts/x.pptx, or rehydrated chat history
   * where the original file block is gone). Clickable: opens
   * `/api/decks/:deckId/context/download?key=<urlencoded>`.
   */
  fileKey?: string;
  fileMime?: string;
  fileSize?: number;
  /**
   * Multimodal image attachments on a user message. Populated by
   * /chat-attachments upload + persisted in the DO's messages
   * table. Rendered as inline thumbnails in the user bubble; the
   * server packs them into Anthropic vision blocks on the next
   * turn.
   */
  attachments?: ChatAttachmentRef[];
  timestamp: number;
  agent?: string; // tulpa name, e.g. 'pp-planner' or 'pp-builder'
  /**
   * Arx agent instance id (UUID) — surfaced on hover via the `pp-agent-id`
   * chip. Populated when the message originates from a live SSE content
   * event; messages restored from the /messages REST endpoint do not
   * carry it (server doesn't persist instance id today).
   */
  agentId?: string;
}

export interface UploadedFile {
  name: string;
  content: string;
  contentType: string;
  size: number;
  rawFile?: File;
}

interface ChatPanelProps {
  messages: ChatMessage[];
  onSend: (content: string, opts?: { intent?: 'agent'|'discussion'; displayContent?: string; messageType?: 'file' | 'folder'; skipLocalMessage?: boolean; attachments?: string[]; imageAttachments?: ChatAttachmentRef[] }) => void;
  onStop: () => void;
  onClearMessages?: () => void;
  onUploadFile?: (file: UploadedFile, onProgress?: (pct: number) => void) => Promise<void>;
  isGenerating: boolean;
  /**
   * Indicates that the SSE stream has been dropped for long enough that the
   * generating indicator should switch from "Generating…" to a "Connection
   * lost — response may be incomplete" variant. Parent is expected to flip
   * this to `true` only when a stream is still in flight and the drop has
   * exceeded the UX threshold (≥10s) — shorter blips remain silent.
   */
  streamingStalled?: boolean;
  activityItems: ActivityItem[];
  /**
   * Per-turn grouped view of activity. Drives the new collapse-on-done
   * timeline: live turns render expanded with a hairline left rule;
   * completed turns collapse to a single summary line.
   */
  turns?: TurnRecord[];
  activityError?: boolean;
  deckId?: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  agentOnline?: boolean;
  agentBooting?: boolean;
  profiles?: Record<string, Profile>;
  feishuOpen?: boolean;
  onToggleFeishu?: () => void;
  referencedPages?: Array<{ id: string; title: string }>;
  slideRefs?: Array<{ deck_id: string; version: string; slide_num: number; deck_title: string }>;
  onRemoveSlideRef?: (r: { deck_id: string; version: string; slide_num: number }) => void;
  onOpenSlidePicker?: () => void;
  recentArtifacts?: Array<{ version: string; filename: string }>;
  onOpenArtifactFile?: (a: { version: string; filename: string }) => void;
  onRemovePageReference?: (pageId: string) => void;
  currentTulpa?: string; // e.g. 'pp-planner'
  /** Display name for the signed-in user — drives the monogram +
   *  label on user-side messages, mirroring the agent's monogram. */
  currentUserName?: string;
  /** Friendly nickname for the bound agent (e.g. "PP Writer"
   *  instead of the slug "pp-writer"). Used as the label on
   *  assistant messages. Falls back to the slug when unset. */
  agentDisplayName?: string;
  onLoadMore?: () => void;
  hasMoreMessages?: boolean;
  loadingMoreMessages?: boolean;
  /**
   * Click handler for assistant-side artifact file cards. When provided,
   * clicking a clickable file card invokes this instead of opening the
   * download URL in a new tab — used by the right-side preview panel to
   * switch into artifact view mode (PPTX metadata card, image/pdf inline,
   * etc.). When omitted, file cards fall back to the legacy `<a target=
   * _blank>` direct-download behaviour.
   */
  onArtifactCardClick?: (artifact: { fileHash: string; filename: string; fileMime?: string; fileSize?: number }) => void;
}

/** Format a byte count as a human-readable size suffix (e.g. "245 KB"). */
function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** In-memory cache for uploaded image blob URLs (survives within session) */
const _imageBlobCache: Record<string, string> = {};

// ── [user-edit] / [user-comment] card rendering ──────────────────────
// PreviewPanel emits two structured user messages: a `[user-edit]`
// marker on in-place HTML save (per-element diff list) and a
// `[user-comment]` marker on anchor-comment send (anchor + request +
// element snippet). Both go to the agent verbatim — but in the user's
// own chat they're rendered as a collapsed card so the timeline stays
// scannable instead of showing a multi-line raw blob. Aligns PP main
// #104.

type EditCardParsed =
  | { kind: 'edit'; slideIds: string[]; noChanges: boolean; diffBody: string }
  | { kind: 'comment'; slideId: string; anchor: string; request: string; elementText: string };

const USER_EDIT_PREFIX = '[user-edit]';
const USER_COMMENT_PREFIX = '[user-comment]';

function extractSlideToken(line: string): string {
  const m = line.match(/\bslide-[A-Za-z0-9_-]+/);
  return m ? m[0] : '';
}

function slideDisplayNumber(token: string): string {
  const m = token.match(/(\d+)\s*$/);
  if (!m) return token.replace(/^slide-/, '');
  return String(parseInt(m[1], 10));
}

// `JSON.stringify`-encoded by PreviewPanel; JSON.parse is the natural
// inverse. Fall back to raw text on malformed payloads so the card
// still renders something useful instead of crashing.
function decodeJsonField(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('"')) {
    try { return JSON.parse(trimmed); } catch { /* fall through */ }
  }
  if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

export function parseEditOrCommentMarker(content: string): EditCardParsed | null {
  if (typeof content !== 'string') return null;
  const trimmed = content.trimStart();

  if (trimmed.startsWith(USER_EDIT_PREFIX)) {
    try {
      const body = trimmed.slice(USER_EDIT_PREFIX.length);
      const noChanges = /no visible text changes detected/i.test(body);
      const slideIds: string[] = [];
      const seen = new Set<string>();
      for (const line of body.split('\n')) {
        if (!line.startsWith('- ')) continue;
        const tok = extractSlideToken(line);
        if (tok && !seen.has(tok)) { seen.add(tok); slideIds.push(tok); }
      }
      // Strip the trailing meta paragraph PreviewPanel attaches for the
      // agent ("This captures the new text but not the user's intent…").
      const metaIdx = body.indexOf('\n\nThis captures');
      const diffBody = (metaIdx >= 0 ? body.slice(0, metaIdx) : body).trim();
      return { kind: 'edit', slideIds, noChanges, diffBody };
    } catch {
      return null;
    }
  }

  if (trimmed.startsWith(USER_COMMENT_PREFIX)) {
    try {
      const body = trimmed.slice(USER_COMMENT_PREFIX.length);
      // PreviewPanel format:
      //   `[user-comment] anchor=...; element_text=...; request=...\n\n<meta>`
      const headEnd = body.indexOf('\n\n');
      const head = (headEnd >= 0 ? body.slice(0, headEnd) : body).trim();
      const anchorMatch = head.match(/anchor=([^;]*)/);
      const elemMatch = head.match(/element_text=("(?:\\.|[^"\\])*"|[^;]*)/);
      const requestMatch = head.match(/request=("(?:\\.|[^"\\])*"|[^;]*)/);
      const anchor = anchorMatch ? anchorMatch[1].trim() : '';
      const elementText = elemMatch ? decodeJsonField(elemMatch[1]) : '';
      const request = requestMatch ? decodeJsonField(requestMatch[1]) : '';
      if (!anchor && !request) return null;
      const slideId = extractSlideToken(anchor);
      return { kind: 'comment', slideId, anchor, request, elementText };
    } catch {
      return null;
    }
  }

  return null;
}

function EditCommentCard({ parsed, fullContent }: { parsed: EditCardParsed; fullContent: string }) {
  const [open, setOpen] = useState(false);

  let title: string;
  let icon: React.ReactNode;
  let inlineBody: React.ReactNode = null;

  if (parsed.kind === 'edit') {
    icon = <PencilSimple size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />;
    if (parsed.noChanges) {
      title = 'Edited (no visible text changes)';
    } else if (parsed.slideIds.length === 0) {
      title = 'Edited slides';
    } else if (parsed.slideIds.length === 1) {
      title = `Edited slide ${slideDisplayNumber(parsed.slideIds[0])}`;
    } else if (parsed.slideIds.length <= 4) {
      title = `Edited slides ${parsed.slideIds.map(slideDisplayNumber).join(', ')}`;
    } else {
      title = `Edited ${parsed.slideIds.length} slides`;
    }
  } else {
    icon = <ChatCircle size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />;
    if (parsed.slideId) title = `Comment on slide ${slideDisplayNumber(parsed.slideId)}`;
    else if (parsed.anchor) title = `Comment on ${parsed.anchor}`;
    else title = 'Comment';
    // Comment request reads better inline than buried behind a click —
    // the user just typed it; they don't need to expand to see it.
    if (parsed.request) {
      inlineBody = (
        <div style={{
          fontSize: 'var(--fs-base)', color: 'var(--text-primary)',
          marginTop: 6, lineHeight: 1.5, whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
        }}>
          {parsed.request}
        </div>
      );
    }
  }

  return (
    <div style={{
      background: 'var(--bg-primary)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '6px 10px',
      maxWidth: '90%', minWidth: 240,
      display: 'flex', flexDirection: 'column', gap: 0,
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', padding: 0,
          display: 'flex', alignItems: 'center', gap: 8,
          cursor: 'pointer', textAlign: 'left', color: 'inherit',
          fontFamily: 'inherit',
        }}
        aria-expanded={open}
      >
        <div style={{
          width: 24, height: 24, borderRadius: 4, flexShrink: 0,
          background: 'var(--bg-primary)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <span style={{
          fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-primary)',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {open
          ? <CaretDown size={12} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          : <CaretRight size={12} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        }
      </button>
      {inlineBody}
      {open && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          {parsed.kind === 'comment' && (
            <>
              {parsed.anchor && (
                <>
                  <div style={{
                    fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
                    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>
                    Anchor
                  </div>
                  <div style={{
                    fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                    marginBottom: 8, fontFamily: 'monospace',
                    overflowWrap: 'break-word',
                  }}>
                    {parsed.anchor}
                  </div>
                </>
              )}
              {parsed.elementText && (
                <>
                  <div style={{
                    fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
                    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}>
                    Element
                  </div>
                  <div style={{
                    fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
                    marginBottom: 8, fontStyle: 'italic',
                    overflowWrap: 'break-word', whiteSpace: 'pre-wrap',
                  }}>
                    “{parsed.elementText}”
                  </div>
                </>
              )}
            </>
          )}
          <div style={{
            fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
            marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
            {parsed.kind === 'edit' ? 'Diff' : 'Sent to agent'}
          </div>
          <pre style={{
            margin: 0,
            fontSize: 'var(--fs-sm)', lineHeight: 1.5,
            color: 'var(--text-secondary)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 4, padding: '8px 10px',
            whiteSpace: 'pre-wrap', overflowWrap: 'break-word',
            fontFamily: 'inherit',
          }}>
            {parsed.kind === 'edit' ? parsed.diffBody : fullContent}
          </pre>
        </div>
      )}
    </div>
  );
}

/** Image that loads via fetch with auth token, then displays as blob URL. Click to preview full size. */
function AuthImage({ fileKey, alt, style }: { fileKey: string; alt: string; style?: React.CSSProperties }) {
  const [src, setSrc] = useState<string | null>(_imageBlobCache[fileKey] || null);
  const [preview, setPreview] = useState(false);
  const deckId = (window as any).__PP_STATE?.activeDeckId;
  useEffect(() => {
    if (src || !deckId || !fileKey) return;
    apiFetch(`/api/decks/${deckId}/context/download?key=${encodeURIComponent(fileKey)}`)
      .then(r => {
        if (r.ok) return r.blob();
        throw new Error('not found');
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        _imageBlobCache[fileKey] = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
  }, [deckId, fileKey, src]);
  if (!src) return <div style={{ ...style, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={16} weight="light" style={{ color: 'var(--text-muted)' }} /></div>;
  return (
    <>
      <img src={src} alt={alt} style={{ ...style, cursor: 'pointer' }} onClick={() => setPreview(true)} />
      {preview && (
        <div
          onClick={() => setPreview(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'var(--ds-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
            animation: 'maFadeIn 0.15s ease',
          }}
        >
          <img src={src} alt={alt} style={{
            maxWidth: '90vw', maxHeight: '90vh',
            borderRadius: 4, objectFit: 'contain',
          }} />
        </div>
      )}
    </>
  );
}

/** Cache a blob URL for an uploaded image (called from handlePaste/handleFileSelect) */
function cacheImageBlob(name: string, file: File) {
  const key = `context/${name}`;
  if (!_imageBlobCache[key]) {
    _imageBlobCache[key] = URL.createObjectURL(file);
  }
}

// Configure marked for chat bubbles
marked.setOptions({ breaks: true, gfm: true });

function renderMarkdown(md: string): string {
  return marked.parse(md) as string;
}

/**
 * High-level category buckets for the activity timeline. PP main
 * PR #106 (v2.29.0) — source of truth.
 *
 * User cares about *what the agent is doing* in human terms —
 * "Researching", "Writing", "Building" — not which specific tool
 * fired. `categorizeTool` collapses many tool names to a handful of
 * categories so a turn renders a clean roll-up like "Writing (5)"
 * instead of N "pp save artifact …" rows. `defaultVisible: false`
 * categories drop out of the live render — they live only in the
 * collapsed turn summary for users who want to inspect history.
 *
 * tool/detail are coalesced to `''` defensively: historical activity
 * rows backfilled from arx can carry `tool: null`, which would
 * `.toLowerCase()`-crash the render (PR #109 / v2.32.0).
 */
export interface ActivityCategory {
  key: 'research' | 'writing' | 'building' | 'reading' | 'hidden' | 'attachment-inbox' | 'attachment-read';
  label: string;
  defaultVisible: boolean;
}

const CATEGORY_RESEARCH: ActivityCategory = { key: 'research', label: 'Researching', defaultVisible: true };
const CATEGORY_WRITING:  ActivityCategory = { key: 'writing',  label: 'Writing',     defaultVisible: true };
const CATEGORY_BUILDING: ActivityCategory = { key: 'building', label: 'Building',    defaultVisible: true };
const CATEGORY_READING:  ActivityCategory = { key: 'reading',  label: 'Reading',     defaultVisible: false };
const CATEGORY_HIDDEN:   ActivityCategory = { key: 'hidden',   label: '',            defaultVisible: false };

export function categorizeTool(tool: string | null | undefined, detail: string | null | undefined): ActivityCategory {
  const t = (tool || '').toLowerCase();
  const d = detail || '';
  if (t === 'list_attachments') return {key:'attachment-inbox',label:'Checking attachments',defaultVisible:true};
  if (t === 'open_attachment') return {key:'attachment-read',label:'Opening attachments',defaultVisible:true};
  if (t === 'websearch' || t === 'webfetch') return CATEGORY_RESEARCH;
  if (t === 'write' || t === 'edit') return CATEGORY_WRITING;
  if (t === 'bash') {
    if (d.startsWith('pp save')) return CATEGORY_WRITING;
    return CATEGORY_BUILDING;
  }
  if (t === 'read' || t === 'glob' || t === 'grep') return CATEGORY_READING;
  if (t === 'toolsearch') return CATEGORY_HIDDEN;
  return CATEGORY_BUILDING;
}

export interface ActivityGroup {
  category: ActivityCategory;
  items: ActivityItem[];
  status: 'running' | 'done';
}

export function groupActivities(items: ActivityItem[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  for (const item of items) {
    const cat = categorizeTool(item.tool, item.detail);
    if (!cat.defaultVisible) continue;
    const last = groups[groups.length - 1];
    if (last && last.category.key === cat.key) {
      last.items.push(item);
      if (item.status === 'running') last.status = 'running';
    } else {
      groups.push({ category: cat, items: [item], status: item.status });
    }
  }
  return groups;
}

/**
 * Render an agent message body, collapsing every `<internal>...</internal>`
 * block into a `ChatInternalBlock` disclosure. When the message has no
 * tags we fall through to the original single-`dangerouslySetInnerHTML`
 * path — keeps behaviour identical for the no-tag case (i.e. chat
 * history written before personas were updated in slice 13).
 *
 * Streaming nuance: while a message is mid-stream and an `<internal>`
 * open tag has been seen but the matching close has not yet arrived,
 * we don't want to flash a placeholder for an unclosed block. In that
 * case we render the markdown content seen so far AS-IS up to the
 * open tag, and hide the in-flight tail. Once the close arrives — or
 * the stream ends — we re-render through the segment path and the
 * collapsible appears.
 *
 * Ported from PitchPilot main PR #105.
 */
function renderAgentMessageBody(content: string, isStreaming: boolean) {
  if (!hasInternalBlock(content)) {
    return (
      <div
        className="chat-md"
        style={{ fontSize: 'var(--fs-lg)', lineHeight: 1.7, overflowWrap: 'break-word', color: 'var(--text-primary)', fontWeight: 400 }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
      />
    );
  }

  if (isStreaming && hasUnclosedInternal(content)) {
    const openIdx = content.indexOf('<internal>');
    const head = openIdx >= 0 ? content.slice(0, openIdx) : content;
    return (
      <div
        className="chat-md"
        style={{ fontSize: 'var(--fs-lg)', lineHeight: 1.7, overflowWrap: 'break-word', color: 'var(--text-primary)', fontWeight: 400 }}
        dangerouslySetInnerHTML={{ __html: renderMarkdown(head) }}
      />
    );
  }

  const segments = parseInternalBlocks(content);
  return (
    <div
      className="chat-md"
      style={{ fontSize: 'var(--fs-lg)', lineHeight: 1.7, overflowWrap: 'break-word', color: 'var(--text-primary)', fontWeight: 400 }}
    >
      {segments.map((seg, i) =>
        seg.kind === 'md' ? (
          <div
            key={i}
            style={{ display: 'contents' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(seg.text) }}
          />
        ) : (
          <ChatInternalBlock key={i} text={seg.text} />
        )
      )}
    </div>
  );
}

export interface ActivityItem {
  id: string;
  tool: string;
  detail: string;
  status: 'running' | 'done';
  ts: number;
  turnId?: string;
  /** Arx agent instance id (UUID) — surfaced for hover-reveal chip. */
  agentId?: string;
}

/**
 * One conversational turn from the agent side: an unordered group of
 * `ActivityItem`s plus the bridge-stamped `summary` once the turn ends.
 *
 * `status: 'live'`  → render expanded with the running step animated.
 * `status: 'done'`  → render collapsed to a single summary line.
 */
export interface TurnRecord {
  turnId: string;
  /** Tulpa display key (`pp-planner`, etc.) — resolved against `profiles`. */
  agent: string;
  /** Arx agent instance id — for hover-reveal. */
  agentId: string;
  items: ActivityItem[];
  status: 'live' | 'done';
  startedAt: number;
  /** From `done` event — past-tense one-liner; "" if turn produced nothing. */
  summary?: string;
  /** From `done` event — wall-clock duration of the turn in milliseconds. */
  durationMs?: number;
}

/** Extract a human-readable detail string from a tool_use event. */
export function extractDetail(tool: string, content: string): string {
  const t = tool.toLowerCase();
  let parsed: Record<string, unknown> | null = null;
  try {
    if (content && content.startsWith('{')) parsed = JSON.parse(content);
  } catch {}

  const basename = (p: string) => p.split('/').pop() || p;

  if (t === 'list_attachments') return 'Checking uploaded attachments';
  if (t === 'open_attachment') return `Opening ${basename(String(parsed?.entry ?? parsed?.key ?? 'attachment'))}`;
  if (t === 'read' && parsed?.file_path) return `Reading ${basename(parsed.file_path as string)}`;
  if (t === 'write' && parsed?.file_path) return `Writing ${basename(parsed.file_path as string)}`;
  if (t === 'edit' && parsed?.file_path) return `Editing ${basename(parsed.file_path as string)}`;
  if (t === 'glob' && parsed?.pattern) return `Scanning ${parsed.pattern}`;
  if (t === 'grep' && parsed?.pattern) return `Searching "${(parsed.pattern as string).slice(0, 30)}"`;
  if (t === 'bash' && parsed?.command) {
    const cmd = (parsed.command as string).trim();
    if (cmd.includes('pp save') || cmd.includes('pp task')) return cmd.replace(/^.*?(pp\s)/, 'pp ');
    if (cmd.includes('curl')) return 'Calling API';
    return cmd;
  }
  if (t === 'webfetch' && parsed?.url) {
    try { return `Fetching ${new URL(parsed.url as string).hostname}`; } catch { return 'Fetching URL'; }
  }
  if (t === 'websearch' && parsed?.query) return `Searching "${(parsed.query as string).slice(0, 30)}"`;
  if (t === 'toolsearch') return 'Loading tools';

  if (t === 'read') return 'Reading file';
  if (t === 'write') return 'Writing file';
  if (t === 'edit') return 'Editing file';
  if (t === 'bash') return 'Running command';
  if (t === 'glob') return 'Scanning files';
  if (t === 'grep') return 'Searching';
  return `Using ${tool}`;
}

// ---- Web Speech API types ----
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  length: number;
  isFinal: boolean;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

function getSpeechRecognition(): (new () => any) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

// ---- File reading helpers ----
const TEXT_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'csv', 'tsv', 'json', 'xml', 'html', 'htm',
  'css', 'js', 'ts', 'jsx', 'tsx', 'py', 'rb', 'go', 'rs', 'java',
  'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'sh', 'bash', 'zsh',
  'sql', 'log', 'env', 'gitignore', 'dockerignore', 'editorconfig',
]);

function isTextFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return TEXT_EXTENSIONS.has(ext);
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export default function ChatPanel({ messages, onSend: onSendRaw, onStop, onClearMessages, onUploadFile, isGenerating, streamingStalled = false, activityItems, turns = [], activityError = false, deckId, collapsed, onToggleCollapse, agentOnline = true, agentBooting = false, profiles = {}, feishuOpen = false, onToggleFeishu, referencedPages = [], onRemovePageReference, slideRefs = [], onRemoveSlideRef, onOpenSlidePicker, recentArtifacts = [], onOpenArtifactFile, currentTulpa, currentUserName, agentDisplayName, onLoadMore, hasMoreMessages = false, loadingMoreMessages = false, onArtifactCardClick }: ChatPanelProps) {
  const conversation = useConversation(deckId);
  const [sendIntent,setSendIntent] = useState<'agent'|'discussion'>('discussion');
  const [input, setInput] = useState('');
  const [modeNotice,setModeNotice] = useState('');
  const previousMode = useRef<{deckId?:string;mode:string}|null>(null);
  const pendingMode = useRef(false);
  useEffect(()=>{
    if(!conversation.ready)return;
    const next=conversation.mode==='personal'?'agent':'discussion';
    if(!previousMode.current||previousMode.current.deckId!==deckId){setSendIntent(next);setModeNotice('');pendingMode.current=false;}
    else if(previousMode.current.mode!==conversation.mode){
      setModeNotice(conversation.mode==='shared'?'This deck is now shared. Check the destination before sending.':'This is now a personal conversation.');
      if(!input.trim())setSendIntent(next);else pendingMode.current=true;
    } else if(pendingMode.current&&!input.trim()){setSendIntent(next);pendingMode.current=false;}
    previousMode.current={deckId,mode:conversation.mode};
  },[conversation.ready,conversation.mode,deckId,input]);
  const onSend: ChatPanelProps['onSend'] = (content,opts) => {
    if(!conversation.canWrite)return;
    onSendRaw(content,{...opts,intent:/^\s*@agent\b/i.test(content)?'agent':opts?.intent??sendIntent});
  };

  const [isListening, setIsListening] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  // Per-file upload progress 0..100. Populated by startUpload via the
  // XHR-backed apiUpload path; falls back to an indeterminate spinner
  // when a key is absent.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  // Single-source helper that tracks an in-flight upload through the
  // uploadingFiles + uploadProgress state arrays. Surfaces the
  // progress callback into onUploadFile so any caller of this
  // component that implements progress reporting (App.tsx via
  // api.uploadContextFile) can stream byte-level updates into the UI.
  // Throws on failure so call sites can decide whether to alert /
  // re-queue / etc.
  const startUpload = useCallback(async (uploadedFile: UploadedFile): Promise<void> => {
    if (!onUploadFile) return;
    const name = uploadedFile.name;
    setUploadingFiles(prev => [...prev, name]);
    setUploadProgress(prev => ({ ...prev, [name]: 0 }));
    try {
      await onUploadFile(uploadedFile, (pct) => {
        setUploadProgress(prev => ({ ...prev, [name]: pct }));
      });
    } finally {
      setUploadingFiles(prev => prev.filter(n => n !== name));
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }, [onUploadFile]);
  // Multimodal image attachments — uploaded to /chat-attachments
  // (different surface from pendingFiles, which goes through the
  // legacy /context/upload path). Rides along on /send so the
  // server can pack them as Anthropic vision blocks.
  const [imageAttachments, setImageAttachments] = useState<ChatAttachmentRef[]>([]);
  const [uploadingImages, setUploadingImages] = useState<number>(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [waitingToast, setWaitingToast] = useState(false);

  // Best-effort image upload — silent on success, toast on failure.
  // No retry; user re-attaches manually if the upload errored.
  const addImageAttachment = useCallback(
    async (file: File) => {
      if (!deckId) return;
      if (imageAttachments.length + uploadingImages >= CHAT_ATTACHMENT_MAX_PER_TURN) {
        setLastError(`Max ${CHAT_ATTACHMENT_MAX_PER_TURN} images per message.`);
        return;
      }
      if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
        setLastError(
          `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MiB). Max ${(CHAT_ATTACHMENT_MAX_BYTES / 1024 / 1024).toFixed(1)} MiB.`,
        );
        return;
      }
      const mime = file.type.toLowerCase();
      if (!CHAT_ATTACHMENT_ACCEPT.split(',').includes(mime)) {
        setLastError(`Unsupported image type: ${mime || '(unknown)'}.`);
        return;
      }
      setUploadingImages((n) => n + 1);
      try {
        const ref = await uploadChatAttachment(deckId, file);
        setImageAttachments((prev) => [...prev, ref]);
      } catch (err) {
        setLastError(`Upload failed: ${(err as Error)?.message ?? 'unknown'}`);
      } finally {
        setUploadingImages((n) => Math.max(0, n - 1));
      }
    },
    [deckId, imageAttachments.length, uploadingImages],
  );

  const removeImageAttachment = useCallback((idx: number) => {
    setImageAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const [lastError, setLastError] = useState<string | null>(null);
  useEffect(() => {
    if (!lastError) return;
    const t = setTimeout(() => setLastError(null), 4000);
    return () => clearTimeout(t);
  }, [lastError]);
  const waitingToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const composingRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const voiceBaseTextRef = useRef('');
  const [typingVisible, setTypingVisible] = useState(true);
  const [elapsed, setElapsed] = useState('');
  const [activityShowCount, setActivityShowCount] = useState(1);
  // Reset to show only latest when new generation starts
  useEffect(() => { if (isGenerating) setActivityShowCount(1); }, [isGenerating]);

  // Elapsed timer — reads start time from sessionStorage so it survives refresh
  useEffect(() => {
    if (!isGenerating && !deckId) {
      setElapsed('');
      return;
    }
    const key = deckId ? `pp_activity_start_${deckId}` : 'pp_activity_start';
    const tick = () => {
      const raw = sessionStorage.getItem(key);
      if (!raw) { setElapsed(''); return; }
      const seconds = Math.floor((Date.now() - Number(raw)) / 1000);
      if (seconds < 0) { setElapsed(''); return; }
      if (seconds < 60) setElapsed(`${seconds}s`);
      else setElapsed(`${Math.floor(seconds / 60)}m${seconds % 60 > 0 ? ` ${seconds % 60}s` : ''}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isGenerating, deckId]);

  const prevMessageCountRef = useRef(messages.length);
  // Mirror count for turns — activity-history backfill finishes a few
  // ms after the chat messages fetch. Without this, the initial-scroll
  // effect fires on messages 0→N but doesn't re-fire when turns 0→M
  // arrives, leaving the latest turn under the composer.
  // (aligns PP main #132 / 2.34.25)
  const prevTurnCountRef = useRef(turns.length);
  // Live-tail engagement. True = follow the bottom as content streams in;
  // False = user scrolled up to read history, leave them be. Two-source
  // truth:
  //   * Updated synchronously on user scroll (handleScroll below) —
  //     covers the common "user wheels up between renders" case.
  //   * Re-checked pre-mutation in the useLayoutEffect below — covers
  //     the race where a stream chunk's setMessages commits BEFORE the
  //     rAF-batched scroll event has fired, which would otherwise leave
  //     the ref stale and yank the user back on every chunk.
  // (aligns PP main #116 / 2.34.4)
  const wasAtBottomRef = useRef(true);

  // Pre-mutation snapshot. useLayoutEffect cleanup fires AFTER React
  // decides to re-render but BEFORE the new DOM commits — so
  // el.scrollHeight here still reflects the OLD content. We re-derive
  // wasAtBottomRef from that pre-mutation measurement so the
  // autoscroll decision below is based on where the user actually was,
  // not on whatever value the throttled scroll listener last wrote.
  useLayoutEffect(() => {
    return () => {
      const el = scrollRef.current;
      if (!el) return;
      wasAtBottomRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    };
  }, [messages, turns]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prevMsgCount = prevMessageCountRef.current;
    const prevTurnCount = prevTurnCountRef.current;
    prevMessageCountRef.current = messages.length;
    prevTurnCountRef.current = turns.length;
    // Initial population — both were empty, now at least one populated.
    // Snap to bottom regardless of wasAtBottomRef (user hasn't had a
    // chance to scroll yet). Covers deck open + activity-history
    // backfill races (#132).
    const wasEmpty = prevMsgCount === 0 && prevTurnCount === 0;
    const isPopulated = messages.length > 0 || turns.length > 0;
    if (wasEmpty && isPopulated) {
      el.scrollTop = el.scrollHeight;
      // Second pass via rAF to catch layout shifts that happen between
      // effect-flush and paint (avatar images decoding, lazy markdown,
      // measured component heights).
      requestAnimationFrame(() => {
        const el2 = scrollRef.current;
        if (el2) el2.scrollTop = el2.scrollHeight;
      });
      return;
    }
    // In-place update (streaming text, load-more) — respect engagement.
    //   1. Load-more — messages prepended; preserve where the user is.
    //   2. Streaming text — agent writing into existing bubble; the
    //      messages reference changes every token but count is unchanged.
    //      Without the guard the effect would scroll on every character.
    if (!wasAtBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, turns]);

  // Draft cards live inside the scroll above the composer. When the
  // user adds a file/reference (or upload progress changes the card
  // height), snap to the new card only if they were already near the
  // bottom — otherwise leave them where they are, same rule as the
  // streaming-text autoscroll above.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!wasAtBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [pendingFiles.length, uploadingFiles.length, referencedPages.length, slideRefs.length, recentArtifacts.length]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    wasAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    // Load more when scrolled near top
    if (el.scrollTop < 80 && hasMoreMessages && !loadingMoreMessages && onLoadMore) {
      onLoadMore();
    }
  }, [hasMoreMessages, loadingMoreMessages, onLoadMore]);

  // Track typing bubble visibility
  useEffect(() => {
    const el = typingRef.current;
    if (!el || !isGenerating) { setTypingVisible(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setTypingVisible(entry.isIntersecting),
      { root: scrollRef.current, threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isGenerating]);

  // Scroll-to-bottom bridge from App.tsx's NewMessagesBanner.
  // Dispatched via CustomEvent to avoid threading a ref through props.
  useEffect(() => {
    const handler = () => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    };
    window.addEventListener('pp:scroll-chat-to-bottom', handler);
    return () => window.removeEventListener('pp:scroll-chat-to-bottom', handler);
  }, []);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
    };
  }, []);

  const resetTextarea = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.overflow = 'hidden';
    }
  };

  const showWaitingToast = () => {
    if (waitingToastTimer.current) clearTimeout(waitingToastTimer.current);
    setWaitingToast(true);
    waitingToastTimer.current = setTimeout(() => setWaitingToast(false), 2500);
  };

  const toolsDisabled = !agentOnline || (messages.length === 0 && isGenerating);

  const handleSend = () => {
    if (toolsDisabled) { showWaitingToast(); return; }
    const text = input.trim();
    if (!text && pendingFiles.length === 0 && imageAttachments.length === 0) return;
    if (uploadingImages > 0) {
      setLastError('Wait for images to finish uploading.');
      return;
    }
    // Sending is the user's explicit "I want to see this" — even if
    // they scrolled up to read history, the autoscroll effect should
    // snap back when their own message lands. Reset the at-bottom ref
    // so the effect doesn't bail on the next messages update.
    wasAtBottomRef.current = true;

    if (pendingFiles.length > 0) {
      const fileNames = pendingFiles.map(f => f.name);
      const fileDisplay = fileNames.join('\n');
      // Single onSend even when text is also present. Two calls produced
      // two persisted rows (one [REF:file] + one plain-text), so on
      // reload expandRefMessage split each of them again — two file
      // cards + one orphaned text bubble for what was intended as one
      // user turn. Pack file metadata + free-text together and let the
      // server persist a single [REF:file]…\n<text> row; expandRefMessage
      // on reload then splits it into the right card+bubble pair
      // (aligns PP main #114).
      onSend(text, {
        displayContent: fileDisplay,
        messageType: 'file',
        attachments: fileNames,
      });
      setPendingFiles([]);
    } else {
      // Image attachments (multimodal) ride on the same /send call as
      // the text; the server packs them into Anthropic vision blocks.
      onSend(text, imageAttachments.length > 0 ? { imageAttachments } : undefined);
    }
    setImageAttachments([]);

    setInput('');
    resetTextarea();
  };

  // Paste handler — supports clipboard images (screenshots).
  // Pasted images go through the multimodal /chat-attachments path
  // so they reach Anthropic vision on the next turn.
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const rawFile = item.getAsFile();
        if (!rawFile) continue;
        const ext = item.type.split('/')[1] || 'png';
        const name = `clipboard-${Date.now()}.${ext}`;
        const namedFile = new File([rawFile], name, { type: rawFile.type });
        await addImageAttachment(namedFile);
        return; // only handle the first image per paste event
      }
    }
  }, [addImageAttachment]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing || composingRef.current) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const resizeTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    const maxH = Math.round(window.innerHeight * 0.382);
    const newH = Math.min(el.scrollHeight, maxH);
    el.style.height = newH + 'px';
    el.style.overflow = el.scrollHeight > maxH ? 'auto' : 'hidden';
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    resizeTextarea(e.target);
  };

  // ---- Voice Input ----
  const toggleVoice = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in this browser. Please use Chrome.');
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';

    // Save current input as the base before voice starts
    const baseText = input;
    voiceBaseTextRef.current = baseText;
    let finalTranscript = '';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      // Rebuild finalTranscript from all final results (not incremental)
      finalTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      // Compose: base text + speech results
      const separator = voiceBaseTextRef.current && !voiceBaseTextRef.current.endsWith(' ') ? ' ' : '';
      const newValue = voiceBaseTextRef.current + separator + finalTranscript + interim;
      setInput(newValue);
      if (inputRef.current) resizeTextarea(inputRef.current);
    };

    recognition.onstart = () => {};

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      recognitionRef.current = null;
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access in your browser settings.');
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  // ---- File Upload ----
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Image MIME → multimodal /chat-attachments path (Anthropic
      // vision on next turn). Everything else falls through to the
      // legacy /context/upload pendingFiles flow below.
      const mime = (file.type || '').toLowerCase();
      if (CHAT_ATTACHMENT_ACCEPT.split(',').includes(mime) && file.size <= CHAT_ATTACHMENT_MAX_BYTES) {
        await addImageAttachment(file);
        continue;
      }

      // Large attachments use bounded multipart uploads.
      if (file.size > MAX_ATTACHMENT_BYTES) {
        alert(`File "${file.name}" is too large (max 200MB).`);
        continue;
      }

      try {
        // If we have a direct upload handler, use rawFile via FormData
        // (no need to read content into memory for binary files)
        if (onUploadFile) {
          const uploadedFile: UploadedFile = {
            name: file.name,
            content: '',
            contentType: file.type || 'application/octet-stream',
            size: file.size,
            rawFile: file,
          };
          try {
            await startUpload(uploadedFile);
            setPendingFiles(prev => [...prev, uploadedFile]);
          } catch (err: any) {
            alert(`Failed to upload "${file.name}": ${err.message}`);
          }
          continue;
        }

        // No upload handler — store file reference only (no content reading)
        setPendingFiles(prev => [...prev, {
          name: file.name,
          content: '',
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          rawFile: file,
        }]);
      } catch (err: any) {
        alert(`Failed to read "${file.name}": ${err.message}`);
      }
    }

    // Reset input so same file can be selected again
    e.target.value = '';
  }, [onUploadFile, startUpload, addImageAttachment]);

  const removePendingFile = useCallback((name: string) => {
    setPendingFiles(prev => prev.filter(f => f.name !== name));
  }, []);

  // ---- Drag & Drop (window-level detection) ----
  useEffect(() => {
    let counter = 0;
    const onEnter = (e: DragEvent) => {
      e.preventDefault();
      counter++;
      if (e.dataTransfer?.types.includes('Files')) setIsDragOver(true);
    };
    const onLeave = (e: DragEvent) => {
      e.preventDefault();
      counter--;
      if (counter === 0) setIsDragOver(false);
    };
    const onOver = (e: DragEvent) => e.preventDefault();
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      counter = 0;
      setIsDragOver(false);
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Image MIME → multimodal path (vision on next turn).
      const mime = (file.type || '').toLowerCase();
      if (CHAT_ATTACHMENT_ACCEPT.split(',').includes(mime) && file.size <= CHAT_ATTACHMENT_MAX_BYTES) {
        await addImageAttachment(file);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        alert(`File "${file.name}" is too large (max 200MB).`);
        continue;
      }
      if (onUploadFile) {
        const uploadedFile: UploadedFile = {
          name: file.name,
          content: '',
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          rawFile: file,
        };
        try {
          await startUpload(uploadedFile);
          setPendingFiles(prev => [...prev, uploadedFile]);
        } catch (err: any) {
          alert(`Failed to upload "${file.name}": ${err.message}`);
        }
      } else {
        setPendingFiles(prev => [...prev, {
          name: file.name,
          content: '',
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          rawFile: file,
        }]);
      }
    }
  }, [onUploadFile, startUpload, addImageAttachment]);

  // Collapsed: show floating FAB
  if (collapsed) {
    return (
      <button
        onClick={onToggleCollapse}
        title="Open chat"
        style={{
          position: 'fixed', bottom: 24, right: 24, width: 40, height: 40,
          borderRadius: 4, background: 'var(--ds-text)', color: 'var(--ds-on-accent)',
          border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'center', boxShadow: 'none',
          zIndex: 100, transition: 'opacity 0.3s',
        }}
        onMouseOver={e => { e.currentTarget.style.opacity = '0.8'; }}
        onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
      >
        <ChatCircle size={16} weight="light" />
        {isGenerating && (
          <span style={{
            position: 'absolute', top: -2, right: -2, width: 8, height: 8,
            borderRadius: '50%', background: 'var(--ds-text-muted)', border: '2px solid white',
          }} />
        )}
      </button>
    );
  }

  /**
   * Collapsed turn summary row. Shown for every completed (status='done')
   * turn in the chronological timeline. Click the caret to inline-expand
   * and see the original step list.
   *
   * Visual contract (from /home/ubuntu/PitchPilot-activity-redesign/docs/iterations/
   * 2026-04-29-activity-timeline-redesign/mockup.html):
   *   ▸ ✓ <summary>     · N actions     <duration>
   * Single-line, monochrome, hairline left rule when expanded.
   */
  const renderTurnSummary = (turn: TurnRecord) => {
    const itemCount = turn.items.length;
    const seconds = turn.durationMs ? Math.round(turn.durationMs / 1000) : null;
    const summary = turn.summary && turn.summary.length > 0
      ? turn.summary
      // Bridge guarantees a non-empty summary whenever the turn produced
      // anything visible, so this branch only fires for truly empty
      // turns (engine boot noise) — fall back to action count.
      : (itemCount > 0 ? `${itemCount} action${itemCount === 1 ? '' : 's'}` : '');
    return (
      <div key={`turn-${turn.turnId}`} className="pp-row" style={{ position: 'relative' }}>
        {turn.agent && (
          <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {profiles[turn.agent]?.display_name || turn.agent}
            {turn.agentId && <span className="pp-agent-id">· {turn.agentId}</span>}
          </div>
        )}
        <details>
          <summary style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 'var(--fs-xs)', fontWeight: 300,
            color: 'var(--text-muted)', padding: '2px 0',
          }}>
            <svg className="pp-caret" viewBox="0 0 8 8" width={8} height={8} fill="currentColor">
              <path d="M2 1l4 3-4 3z" />
            </svg>
            <span aria-hidden="true">✓</span>
            <span style={{ color: 'var(--text-secondary)' }}>{summary}</span>
            {itemCount > 0 && summary !== `${itemCount} action${itemCount === 1 ? '' : 's'}` && (
              <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }}>
                · {itemCount} action{itemCount === 1 ? '' : 's'}
              </span>
            )}
            {seconds !== null && (
              <span style={{ marginLeft: 'auto', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' }}>
                {seconds}s
              </span>
            )}
          </summary>
          <div style={{
            borderLeft: '1px solid var(--border)',
            margin: '4px 0 4px 3px',
            padding: '4px 0 4px 13px',
          }}>
            {/* Category roll-up (PP main PR #106): collapse N tool
                rows into one row per logical category. Items inside
                a category render below the header for users who
                expand the disclosure. */}
            {groupActivities(turn.items).map(group => {
              const label = group.items.length > 1
                ? `${group.category.label} (${group.items.length})`
                : group.category.label;
              return (
                <div key={group.category.key} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                  fontSize: 'var(--fs-xs)', fontWeight: 300,
                  color: group.status === 'done' ? 'var(--text-muted)' : 'var(--text-secondary)',
                  opacity: group.status === 'done' ? 0.6 : 1,
                  padding: '1px 0',
                }}>
                  <span style={{ fontSize: 9, marginTop: 2, flexShrink: 0 }}>✓</span>
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{label}</span>
                </div>
              );
            })}
          </div>
        </details>
      </div>
    );
  };

  /**
   * Single chat message row — extracted from the previous inline
   * `messages.map(...)` so we can interleave it with turn summaries.
   *
   * Behavior preserved verbatim from the prior implementation:
   *   - system role → centered muted line
   *   - user role  → right-anchored column, no bubble (file/folder/text variants)
   *   - assistant  → left-aligned, optional uppercase agent label, markdown body
   *
   * Addition: `pp-row` + `pp-agent-id` classes drive the hover-reveal
   * agent_id chip (CSS in app/index.html).
   */
  /**
   * Render a single file/folder card. Used for both user-uploaded
   * attachments (legacy `[REF:file]` text markers — non-clickable,
   * `fileHash` undefined) and agent-emitted Block::File payloads
   * (clickable, opens the artifact via the deck-scoped proxy).
   */
  const renderFileCard = (key: number | string, name: string, opts: { isFolder?: boolean; fileHash?: string; fileKey?: string; fileMime?: string; fileSize?: number }) => {
    const isImage = /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(name);
    if (isImage && !opts.fileHash && !opts.fileKey) {
      // Legacy user-attachment image — pulled from context memory
      return <AuthImage key={key} fileKey={`context/${name}`} alt={name} style={{ maxWidth: 240, maxHeight: 320, borderRadius: 8, objectFit: 'contain' }} />;
    }
    // Two clickable paths, in priority order:
    //  1. fileHash — content-addressed; routes through the artifact
    //     viewer overlay if the parent wired one, else direct download.
    //  2. fileKey — path-addressed (context key); plain new-tab
    //     download via the existing /context/download proxy. Folder
    //     cards stay non-clickable (no folder-zip endpoint).
    const clickableHash = !!(opts.fileHash && deckId);
    const clickableKey = !clickableHash && !!(opts.fileKey && deckId && !opts.isFolder);
    const clickable = clickableHash || clickableKey;
    const href = clickableHash
      ? `/api/decks/${deckId}/artifacts/${encodeURIComponent(opts.fileHash!)}?download=1${opts.fileMime ? `&mime=${encodeURIComponent(opts.fileMime)}` : ''}`
      : clickableKey
        ? `/api/decks/${deckId}/context/download?key=${encodeURIComponent(opts.fileKey!)}`
        : undefined;
    // When the parent has wired an artifact-view handler AND we have a
    // hash, intercept the click and let the right-side preview panel
    // handle rendering. fileKey-only cards always fall through to the
    // <a download> path.
    const useViewer = clickableHash && !!onArtifactCardClick;
    const handleViewerClick = useViewer
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          onArtifactCardClick!({
            fileHash: opts.fileHash!,
            filename: name,
            fileMime: opts.fileMime,
            fileSize: opts.fileSize,
          });
        }
      : undefined;
    const card = (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: 'var(--bg-primary)', border: '1px solid var(--border)',
        borderRadius: 4, padding: '6px 10px',
        cursor: clickable ? 'pointer' : 'default',
        textDecoration: 'none', color: 'inherit',
      }}>
        <div style={{
          width: 24, height: 24, borderRadius: 4, flexShrink: 0,
          background: 'var(--bg-primary)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {opts.isFolder
            ? <FolderSimple size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />
            : <FileText size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />
          }
        </div>
        <span style={{
          fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{name}</span>
        {opts.fileSize !== undefined && (
          <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
            {formatBytes(opts.fileSize)}
          </span>
        )}
      </div>
    );
    if (useViewer) {
      return (
        <button
          key={key}
          onClick={handleViewerClick}
          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}
        >
          {card}
        </button>
      );
    }
    return clickable
      ? <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{card}</a>
      : <React.Fragment key={key}>{card}</React.Fragment>;
  };

  // Draft cards — rendered at the tail of the chat scroll, just above
  // the composer. These represent attachments and references the user
  // has staged but not yet sent:
  //   - pendingFiles: uploaded successfully, waiting on Send
  //   - uploadingFiles: still streaming bytes (with progress bar)
  //   - referencedPages: Confluence pages linked to this deck
  // Cards share visual language with sent-message file cards (same
  // border, icon, font) but carry an × button + dashed left accent
  // to signal "not yet sent / removable". Self-only — never broadcast
  // to other deck members.
  const renderDraftCards = () => {
    const hasAny =
      pendingFiles.length > 0 ||
      uploadingFiles.length > 0 ||
      referencedPages.length > 0 ||
      slideRefs.length > 0 ||
      recentArtifacts.length > 0;
    if (!hasAny) return null;

    const cardBase: React.CSSProperties = {
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'var(--bg-primary)',
      border: '1px solid var(--border)',
      borderLeft: '2px dashed var(--brand)',
      borderRadius: 4, padding: '6px 10px',
      position: 'relative',
      overflow: 'hidden',
    };
    const iconBox: React.CSSProperties = {
      width: 24, height: 24, borderRadius: 4, flexShrink: 0,
      background: 'var(--bg-primary)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
    const nameStyle: React.CSSProperties = {
      fontSize: 'var(--fs-sm)', fontWeight: 400, color: 'var(--text-primary)',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      maxWidth: 280,
    };
    const removeBtnStyle: React.CSSProperties = {
      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
      color: 'var(--text-muted)', display: 'flex', borderRadius: 4,
      marginLeft: 'auto',
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        {pendingFiles.map(f => {
          const isImage = f.contentType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(f.name);
          const thumbUrl = isImage && f.rawFile ? URL.createObjectURL(f.rawFile) : null;
          return (
            <div key={`draft-file-${f.name}`} style={cardBase}>
              <div style={iconBox}>
                {thumbUrl ? (
                  <img src={thumbUrl} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 3 }} />
                ) : (
                  <FileText size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />
                )}
              </div>
              <span style={nameStyle}>{f.name}</span>
              {f.size !== undefined && (
                <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {formatBytes(f.size)}
                </span>
              )}
              <button
                onClick={() => removePendingFile(f.name)}
                style={removeBtnStyle}
                title="Remove attachment"
                onMouseOver={e => (e.currentTarget.style.color = 'var(--text-primary)')}
                onMouseOut={e => (e.currentTarget.style.color = 'var(--text-muted)')}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          );
        })}

        {uploadingFiles.map(name => {
          const pct = uploadProgress[name];
          const showBar = typeof pct === 'number' && pct < 100;
          return (
            <div key={`draft-uploading-${name}`} style={cardBase}>
              <div style={iconBox}>
                <SpinnerGap size={12} weight="light" className="spin" style={{ color: 'var(--text-secondary)' }} />
              </div>
              <span style={nameStyle}>{name}</span>
              {typeof pct === 'number' && (
                <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {pct}%
                </span>
              )}
              {showBar && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0,
                  height: 2, width: `${pct}%`,
                  background: 'var(--brand)',
                  transition: 'width 0.15s linear',
                }} />
              )}
            </div>
          );
        })}

        {recentArtifacts.map(a => (
          <span
            key={`ra-${a.version}-${a.filename}`}
            onClick={() => onOpenArtifactFile?.(a)}
            title="Open artifact"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'var(--ds-success-soft)', border: '1px solid var(--ds-success-soft)', borderRadius: 10, padding: '2px 8px', margin: '0 4px 4px 0', cursor: 'pointer' }}
          >🖼 {a.filename} <span style={{ opacity: 0.55 }}>({a.version})</span></span>
        ))}
        {slideRefs.map(r => (
          <span key={`sr-${r.deck_id}-${r.version}-${r.slide_num}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'var(--ds-accent-soft)', border: '1px solid var(--ds-accent-soft)', borderRadius: 10, padding: '2px 8px', margin: '0 4px 4px 0' }}>
            ⧉ {r.deck_title} · slide {r.slide_num}
            {onRemoveSlideRef && (
              <span onClick={() => onRemoveSlideRef(r)} style={{ cursor: 'pointer', opacity: 0.6 }}>×</span>
            )}
          </span>
        ))}
        {referencedPages.map(p => (
          <div key={`draft-page-${p.id}`} style={cardBase}>
            <div style={iconBox}>
              <CloudArrowDown size={12} weight="light" style={{ color: 'var(--text-secondary)' }} />
            </div>
            <span style={nameStyle}>{p.title}</span>
            <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
              Confluence
            </span>
            <button
              onClick={() => onRemovePageReference?.(p.id)}
              style={removeBtnStyle}
              title="Remove reference"
              onMouseOver={e => (e.currentTarget.style.color = 'var(--text-primary)')}
              onMouseOut={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  const renderMessageRow = (msg: ChatMessage, profilesMap: Record<string, Profile>) => (
    <div key={msg.id} className="pp-row" style={{ position: 'relative' }}>
      {msg.role === 'system' ? (
        <div style={{ fontSize: 'var(--fs-base)', color: 'var(--ds-text-muted)', textAlign: 'center', padding: '4px 0' }}>
          {msg.content}
        </div>
      ) : msg.role === 'user' ? (() => {
        // [user-edit] / [user-comment] cards take precedence over the
        // plain bubble. Parse lazily; a malformed payload falls through
        // to the raw-text rendering below so a bad parse never blanks
        // chat. Skipped for file/folder rows — those use renderFileCard.
        const editComment =
          msg.messageType !== 'file' && msg.messageType !== 'folder'
            ? (() => {
                const parsed = parseEditOrCommentMarker(msg.content);
                return parsed ? <EditCommentCard parsed={parsed} fullContent={msg.content} /> : null;
              })()
            : null;
        // Initials for the user monogram. Strip non-alphanumeric chars
        // first so an email like "bt-1778@example.com" → "BT". Falls
        // back to "You" if we have no name to derive from.
        const userLabel = msg.author_name || 'Member';
        const userInitials = (userLabel.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(/\s+/).slice(0, 2)
          .map((s) => s[0]?.toUpperCase() || '').join('') || 'U');
        return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {/* User monogram + label — mirrors the agent's header layout
              but with label-then-monogram order so the avatar sits on
              the outer (right) edge of the right-anchored bubble. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 500, color: 'var(--text-secondary)' }}>
              {userLabel}
            </span>
            <span
              style={{
                width: 18, height: 18, borderRadius: 4,
                background: 'var(--text-primary)',
                color: 'var(--bg-primary)',
                fontSize: 9, fontWeight: 600, lineHeight: 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'inherit',
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {userInitials}
            </span>
          </div>
          {msg.attachments && msg.attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end', maxWidth: '90%' }}>
              {msg.attachments.map((att) => (
                <a
                  key={att.r2_key}
                  href={deckId ? chatAttachmentDownloadUrl(deckId, att.r2_key) : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`${att.name} · ${(att.size / 1024).toFixed(0)} KiB`}
                  style={{ display: 'inline-block', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}
                >
                  <img
                    src={deckId ? chatAttachmentDownloadUrl(deckId, att.r2_key) : ''}
                    alt={att.name}
                    style={{ width: 120, height: 120, objectFit: 'cover', display: 'block' }}
                  />
                </a>
              ))}
            </div>
          )}
          {editComment ? (
            editComment
          ) : msg.messageType === 'file' || msg.messageType === 'folder' ? (
            <>
              {msg.fileHash
                // New file-block path: one card with the persisted
                // hash. Filename comes from displayContent only.
                ? renderFileCard(0, msg.displayContent || msg.content, {
                    isFolder: msg.messageType === 'folder',
                    fileHash: msg.fileHash,
                    fileMime: msg.fileMime,
                    fileSize: msg.fileSize,
                  })
                // Legacy [REF:file] path: each line is one card.
                // fileKey lifts the rehydrated history case into the
                // clickable codepath (path-addressed download).
                : (msg.displayContent || msg.content).split('\n').filter(Boolean).map((line, i) => {
                    const name = line.split('/').pop() || line;
                    return renderFileCard(i, name, {
                      isFolder: msg.messageType === 'folder',
                      ...(msg.messageType === 'file' && msg.fileKey ? { fileKey: msg.fileKey } : {}),
                    });
                  })
              }
            </>
          ) : (msg.content || msg.displayContent) ? (
            <div style={{ background: 'transparent', border: 'none', borderRadius: 0, padding: '6px 0', maxWidth: '90%' }}>
              <p style={{ fontSize: 'var(--fs-lg)', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', lineHeight: 1.7, fontWeight: 400, color: 'var(--text-primary)', textAlign: 'left' }}>{msg.displayContent || msg.content}</p>
            </div>
          ) : null}
        </div>
        );
      })() : (() => {
        // Resolve the effective slug for monogram initials. Older
        // messages from solo-mode pp-writer carry no per-message agent
        // label, so we fall back to the deck's bound tulpa.
        const effectiveAgent = msg.agent || currentTulpa || 'pp-writer';
        // Friendly name for the header: prefer the resolved tulpa
        // display name from the agent profile (e.g. "PP Writer") so
        // we don't show the slug uppercased.
        const p = effectiveAgent ? profilesMap[effectiveAgent] : undefined;
        const name = p?.display_name
          || (msg.agent ? undefined : agentDisplayName)
          || effectiveAgent.split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ');
        const isFileCard = msg.messageType === 'file' || msg.messageType === 'folder';
        return (
          <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start', flexDirection: 'column' }}>
            {effectiveAgent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {/* Monogram avatar — display only, mirrors the
                    monogram on user-side messages. The earlier
                    clickable variant opened a profile popover; that
                    popover was retired so this is now plain text. */}
                <span
                  style={{
                    width: 18, height: 18, borderRadius: 4,
                    background: 'var(--brand, #0033FF)',
                    color: 'var(--ds-on-accent)',
                    fontSize: 9, fontWeight: 600, lineHeight: 1,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'inherit',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {effectiveAgent.split('-').slice(0, 2).map((s) => s[0]?.toUpperCase() || '').join('') || 'A'}
                </span>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {name}
                  {msg.agentId && <span className="pp-agent-id" style={{ fontWeight: 400, color: 'var(--text-muted)' }}>· {msg.agentId}</span>}
                </span>
              </div>
            )}
            {isFileCard ? (
              renderFileCard(0, msg.displayContent || msg.content, {
                isFolder: msg.messageType === 'folder',
                fileHash: msg.fileHash,
                fileKey: msg.fileKey,
                fileMime: msg.fileMime,
                fileSize: msg.fileSize,
              })
            ) : (
              <div style={{ background: 'transparent', border: 'none', borderRadius: 0, padding: '0', maxWidth: '90%' }}>
                {renderAgentMessageBody(msg.content, !!msg.id?.startsWith('streaming-'))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );

  return (
    <div className="coastal-chat"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-primary)', position: 'relative' }}
      onDragOver={e => e.preventDefault()}
      onDrop={handleDrop}
    >
      {/* Drag overlay — Apple-style frosted glass */}
      {isDragOver && (
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            position: 'absolute', inset: 0, zIndex: 50,
            background: 'var(--ds-overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'dropzone-in 0.3s ease',
          }}
        >
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
            padding: '36px 48px', borderRadius: 0,
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            boxShadow: 'none',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 4,
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Paperclip size={20} weight="light" style={{ color: 'var(--text-secondary)' }} />
            </div>
            <span style={{ fontSize: 'var(--fs-base)', fontWeight: 400, color: 'var(--text-primary)' }}>
              Drop files to upload
            </span>
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', fontWeight: 300 }}>
              Documents, images and more — up to 200 MB
            </span>
          </div>
        </div>
      )}
      <style>{`
        @keyframes dropzone-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      {/* Messages */}
      <div className="coastal-messages" ref={scrollRef} onScroll={handleScroll} style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720, width: '100%', margin: '0 auto' }}>
        {hasMoreMessages && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            {loadingMoreMessages ? (
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>Loading...</span>
            ) : (
              <button
                onClick={onLoadMore}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--fs-sm)' }}
              >
                Load older messages
              </button>
            )}
          </div>
        )}
        {messages.length === 0 && (agentBooting || isGenerating) && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              textAlign: 'center', padding: '32px 28px', background: 'var(--bg-primary)',
              borderRadius: 0, border: 'none', maxWidth: 280,
            }}>
              <div style={{
                width: 20, height: 20,
                margin: '0 auto 16px', position: 'relative',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  border: '1.5px solid var(--border)',
                  borderTopColor: 'var(--text-muted)',
                  animation: 'spin 1s linear infinite',
                }} />
              </div>
              <p style={{ fontSize: 'var(--fs-sm)', fontWeight: 300, color: 'var(--text-muted)', marginBottom: 0 }}>
                Getting ready
              </p>
            </div>
          </div>
        )}
        {/* Quick start suggestions — shown when chat is empty and agent is ready */}
        {messages.length === 0 && turns.length === 0 && !agentBooting && !isGenerating && agentOnline && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ maxWidth: 360, padding: '0 20px' }}>
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', textAlign: 'center', marginBottom: 20, fontWeight: 300 }}>
                What would you like to present? Pick one or type your own.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  'Help me create a pitch deck for our Series B fundraising',
                  'I need a product intro presentation for a client meeting',
                  'Make a 5-slide internal update on our Q1 progress',
                ].map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => onSend(prompt)}
                    style={{
                      padding: '10px 14px', fontSize: 'var(--fs-sm)', fontWeight: 400,
                      color: 'var(--text-secondary)', background: 'var(--bg-surface)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      transition: 'all 0.15s', lineHeight: 1.5,
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {(() => {
          // Single chronological timeline: messages + completed turns,
          // ordered by timestamp. Live turns render below in the typing
          // area — they belong to the in-flight cursor, not history.
          type Item =
            | { kind: 'msg'; ts: number; msg: ChatMessage }
            | { kind: 'turn'; ts: number; turn: TurnRecord };
          const items: Item[] = [
            ...messages.map(m => ({ kind: 'msg' as const, ts: m.timestamp, msg: m })),
            ...turns
              .filter(t => t.status === 'done')
              .map(t => ({ kind: 'turn' as const, ts: t.startedAt, turn: t })),
          ];
          items.sort((a, b) => a.ts - b.ts);

          return items.map(it => it.kind === 'turn'
            ? renderTurnSummary(it.turn)
            : renderMessageRow(it.msg, profiles));
        })()}

        {/* Live turn block — the in-flight cursor. Renders the latest
            turn whose status is still 'live' as an indented column with
            a hairline left rule; the most-recent running step animates
            via the existing typing-bounce keyframe. Falls back to the
            legacy flat `activityItems` list if no turn is yet attached
            (e.g. arx hasn't emitted the first activity event for the
            new turn). */}
        {isGenerating && (() => {
          const liveTurn = turns.find(t => t.status === 'live');
          // PP main PR #106: live-turn render is a category rollup.
          // One row per logical category instead of one row per tool
          // call. Hidden categories (file reads, tool loads) drop
          // out of the live view so the user sees "Researching" or
          // "Writing (5)" instead of N noisy lines. activityShowCount
          // is no longer needed for the live view.
          const sourceItems = liveTurn ? liveTurn.items : activityItems;
          const groups = groupActivities(sourceItems);

          return (
            <div ref={typingRef} className="pp-row" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {liveTurn?.agent && (
                <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {profiles[liveTurn.agent]?.display_name || liveTurn.agent}
                  {liveTurn.agentId && <span className="pp-agent-id">· {liveTurn.agentId}</span>}
                </div>
              )}
              <div style={{
                borderLeft: liveTurn ? '1px solid var(--border)' : 'none',
                paddingLeft: liveTurn ? 13 : 0,
                marginLeft: liveTurn ? 3 : 0,
                display: 'flex', flexDirection: 'column', gap: 2,
              }}>
                {groups.map(group => {
                  const label = group.items.length > 1
                    ? `${group.category.label} (${group.items.length})`
                    : group.category.label;
                  return (
                    <div key={group.category.key} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 6,
                      fontSize: 'var(--fs-xs)', fontWeight: 300,
                      color: group.status === 'done' ? 'var(--text-muted)' : 'var(--text-secondary)',
                      opacity: group.status === 'done' ? 0.6 : 1,
                      transition: 'opacity 0.3s',
                    }}>
                      {group.status === 'done' ? (
                        <span style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }}>✓</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 3, alignItems: 'center', marginTop: 4, flexShrink: 0 }}>
                          {[0, 1, 2].map(i => (
                            <div key={i} style={{
                              width: 3, height: 3, borderRadius: '50%', background: 'var(--text-muted)',
                              animation: 'typing-bounce 1.4s ease-in-out infinite',
                              animationDelay: `${i * 0.16}s`,
                            }} />
                          ))}
                        </div>
                      )}
                      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{label}</span>
                    </div>
                  );
                })}
              </div>
              {/* Typing dots + elapsed. When the stream is stalled (SSE dropped for
                  ≥10s while a turn is in flight), replace the dots with a muted
                  message — "Generating…" would be a lie at that point since no
                  bytes are flowing from the agent. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: groups.length > 0 ? 4 : 0 }}>
                {activityError ? (
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ds-danger)' }}>⚠</span>
                ) : streamingStalled ? (
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ds-warning)', fontWeight: 400 }}>
                    Connection lost — response may be incomplete
                  </span>
                ) : (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        width: 4, height: 4, borderRadius: '50%', background: 'var(--text-muted)',
                        animation: 'typing-bounce 1.4s ease-in-out infinite',
                        animationDelay: `${i * 0.16}s`,
                      }} />
                    ))}
                  </div>
                )}
                {elapsed && !streamingStalled && (
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>
                    {elapsed}
                  </span>
                )}
              </div>
            </div>
          );
        })()}
        {renderDraftCards()}
      </div>

      {/* Peek indicator — shows when typing bubble is scrolled out of view */}
      {isGenerating && !typingVisible && (
        <div
          onClick={() => {
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }}
          style={{
            position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
            zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px',
            background: 'var(--ds-overlay)',
            borderRadius: 16,
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'opacity 0.3s',
          }}
        >
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: '50%', background: 'var(--ds-overlay)',
                animation: 'typing-bounce 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.16}s`,
              }} />
            ))}
          </div>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ds-overlay)', fontWeight: 500 }}>
            Agent is working
          </span>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ds-overlay)' }}>↓</span>
        </div>
      )}

      {/* Input area */}
      <div className="v8ma-input-area" style={{ padding: '10px 14px 14px', borderTop: 'none', flexShrink: 0, maxWidth: 720, width: '100%', margin: '0 auto' }}>
        {/* Buttons always visible in the input row */}

        <div className="coastal-composer" style={{
          background: 'var(--bg-primary)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '10px 12px 8px',
          transition: 'border-color 0.3s',
        }}>
          {/* pendingFiles previously rendered here as pills; now in
              inline draft cards inside the chat scroll (renderDraftCards). */}

          {/* Image attachments (multimodal). Independent from
              pendingFiles above — these ride along on /send and
              reach Anthropic vision on the next turn. */}
          {(imageAttachments.length > 0 || uploadingImages > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {imageAttachments.map((att, idx) => (
                <div
                  key={att.r2_key}
                  title={`${att.name} · ${(att.size / 1024).toFixed(0)} KiB`}
                  style={{
                    position: 'relative',
                    background: 'var(--bg-primary)', border: '1px solid var(--border)',
                    borderRadius: 6, overflow: 'hidden',
                  }}
                >
                  <img
                    src={deckId ? chatAttachmentDownloadUrl(deckId, att.r2_key) : ''}
                    alt={att.name}
                    style={{ width: 56, height: 56, objectFit: 'cover', display: 'block' }}
                  />
                  <button
                    onClick={() => removeImageAttachment(idx)}
                    title="Remove"
                    style={{
                      position: 'absolute', top: 2, right: 2,
                      background: 'var(--ds-overlay)', border: 'none',
                      cursor: 'pointer', padding: 2, color: 'var(--ds-on-accent)',
                      display: 'flex', borderRadius: '50%',
                    }}
                  >
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ))}
              {uploadingImages > 0 && (
                <div style={{
                  width: 56, height: 56, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'var(--bg-primary)', border: '1px dashed var(--border)',
                  borderRadius: 6, color: 'var(--text-muted)',
                }}>
                  <SpinnerGap size={16} weight="light" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              )}
            </div>
          )}
          {lastError && (
            <div style={{
              marginBottom: 8, padding: '4px 8px', borderRadius: 4,
              background: 'var(--ds-danger-soft)',
              color: 'var(--text-secondary)',
              fontSize: 'var(--fs-2xs)',
            }}>
              {lastError}
            </div>
          )}

          {/* referencedPages + uploadingFiles previously rendered
              here; now in inline draft cards inside the chat scroll
              (renderDraftCards). */}

          {/* Voice listening indicator */}
          {isListening && (
            <div style={{
              fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} style={{
                    width: 1.5, height: 6 + Math.random() * 6, background: 'var(--ds-text-muted)', borderRadius: 1,
                    animation: `pulse 0.8s ease-in-out infinite`, animationDelay: `${i * 0.1}s`,
                  }} />
                ))}
              </div>
              Listening... speak now
            </div>
          )}

          {/* Textarea — explicit non-credential signals so Safari/iCloud
              Keychain and password managers (1Password, LastPass) don't
              speculatively offer an autofill prompt on a chat composer.
              An unnamed bare textarea triggered iCloud Password fill in
              the wild (aligns PP main #94). */}
          {modeNotice&&<p role="status" style={{fontSize:"var(--fs-sm)",color:"var(--text-secondary)"}}>{modeNotice}</p>}
          <ConversationControls deckId={deckId} conversation={conversation} intent={sendIntent} onIntent={setSendIntent} />
          <textarea
            ref={inputRef}
            name="chat-message"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={true}
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => { composingRef.current = true; }}
            onCompositionEnd={() => { composingRef.current = false; }}
            onPaste={handlePaste}
            disabled={!conversation.canWrite}
            placeholder={isListening ? 'Listening...' : sendIntent === 'agent' ? 'Ask Agent to help…' : 'Discuss with your team · @Agent to delegate'}
            rows={1}
            style={{
              display: 'block', width: '100%', background: 'transparent',
              fontSize: 'var(--fs-lg)', resize: 'none', border: 'none', outline: 'none',
              fontFamily: 'inherit', lineHeight: '20px', padding: 0, color: 'var(--text-primary)',
              minHeight: 20, maxHeight: 'calc(38.2vh)', overflow: 'hidden',
            }}
          />

          {/* Toolbar row — icons left, send right */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Feishu button */}
              {onToggleFeishu && (() => (
                <button
                  onClick={toolsDisabled ? () => showWaitingToast() : onToggleFeishu}
                  disabled={false}
                  title="Feishu — Reference docs"
                  style={{
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0,
                    background: feishuOpen ? 'var(--ds-accent-soft)' : 'none',
                    border: feishuOpen ? '1px solid var(--ds-accent-soft)' : '1px solid transparent',
                    cursor: toolsDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: 6, flexShrink: 0, transition: 'all 0.12s',
                    opacity: feishuOpen ? 1 : 0.55,
                  }}
                  onMouseOver={e => { if (!feishuOpen) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--ds-accent-soft)'; } }}
                  onMouseOut={e => { if (!feishuOpen) { e.currentTarget.style.opacity = '0.55'; e.currentTarget.style.background = 'none'; } }}
                >
                  <FeishuIcon size={18} />
                </button>
              ))()}
              {/* Reference-slides picker */}
              {onOpenSlidePicker && (
                <button
                  onClick={toolsDisabled ? () => showWaitingToast() : onOpenSlidePicker}
                  title="Reference slides from workspace decks"
                  style={{
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, background: slideRefs.length > 0 ? 'var(--ds-accent-soft)' : 'none',
                    border: slideRefs.length > 0 ? '1px solid var(--ds-accent-soft)' : '1px solid transparent',
                    cursor: toolsDisabled ? 'not-allowed' : 'pointer', borderRadius: 6, flexShrink: 0,
                    opacity: slideRefs.length > 0 ? 1 : 0.55, fontSize: 13,
                  }}
                >⧉</button>
              )}
              {/* File upload button */}
              <button
                onClick={() => toolsDisabled ? showWaitingToast() : fileInputRef.current?.click()}
                title="Upload file"
                style={{
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none',
                  cursor: toolsDisabled ? 'not-allowed' : 'pointer',
                  color: 'var(--text-muted)',
                  opacity: toolsDisabled ? 0.4 : 1,
                  borderRadius: 4, flexShrink: 0,
                }}
                onMouseOver={e => !toolsDisabled && (e.currentTarget.style.color = 'var(--text-primary)')}
                onMouseOut={e => (e.currentTarget.style.color = 'var(--text-muted)')}
              >
                <Paperclip size={16} weight="light" />
              </button>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md,.csv,.json,.pdf,.docx,.html,.xml,.yaml,.yml,.toml,.py,.js,.ts,.jsx,.tsx,.sql,.log,.sh,.zip,.rar,.7z,.tar,.gz,.png,.jpg,.jpeg,.gif,.svg,.bmp,.webp,.mp3,.mp4,.wav,.xls,.xlsx,.ppt,.pptx,.doc"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', userSelect: 'none' }}>↵</span>
              <button onClick={handleSend} style={{
                width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent',
                color: input.trim() && agentOnline ? 'var(--text-primary)' : 'var(--text-muted)',
                borderRadius: 4, border: 'none', cursor: !agentOnline ? 'not-allowed' : 'pointer', flexShrink: 0,
                transition: 'color 0.3s, opacity 0.3s',
                opacity: input.trim() && agentOnline ? 1 : 0.3,
              }}>
                <PaperPlaneRight size={14} weight="light" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting toast */}
      {waitingToast && (
        <div style={{
          position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--ds-overlay)', color: 'var(--ds-on-accent)', padding: '8px 16px', borderRadius: 16,
          fontSize: 'var(--fs-sm)', whiteSpace: 'nowrap', zIndex: 100,
          animation: 'fadeIn 0.3s ease',
          boxShadow: 'none',
        }}>
          Agent is on the way, please wait...
        </div>
      )}
      <style>{`
        .chat-md h1 { font-size: 1.3rem; font-weight: 700; margin: 12px 0 6px; }
        .chat-md h2 { font-size: 1.15rem; font-weight: 600; margin: 10px 0 4px; }
        .chat-md h3 { font-size: 1.05rem; font-weight: 600; margin: 8px 0 4px; }
        .chat-md p { margin: 4px 0; }
        .chat-md ul, .chat-md ol { padding-left: 20px; margin: 4px 0; }
        .chat-md li { margin: 2px 0; }
        .chat-md code {
          background: var(--code-bg, var(--ds-shadow-color));
          padding: 1px 5px; border-radius: 4px; font-size: 0.88em;
          font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
        }
        .chat-md pre {
          background: var(--code-bg, var(--ds-shadow-color));
          padding: 12px 14px; border-radius: 8px; overflow-x: auto;
          margin: 8px 0; font-size: 0.88em; line-height: 1.5;
        }
        .chat-md pre code { background: none; padding: 0; font-size: inherit; }
        .chat-md blockquote {
          border-left: 3px solid var(--border); padding-left: 12px;
          margin: 8px 0; color: var(--text-secondary);
        }
        .chat-md hr { border: none; border-top: 1px solid var(--border); margin: 12px 0; }
        .chat-md a { color: var(--brand, #0033FF); text-decoration: none; }
        .chat-md a:hover { text-decoration: underline; }
        .chat-md table { border-collapse: collapse; margin: 8px 0; font-size: 0.9em; }
        .chat-md th, .chat-md td { border: 1px solid var(--border); padding: 6px 10px; text-align: left; }
        .chat-md th { background: var(--bg-surface, #f5f5f3); font-weight: 600; }
        .chat-md img { max-width: 100%; border-radius: 6px; }
        .chat-md strong { font-weight: 600; }
      `}</style>
    </div>
  );
}
